// EventSetlist.js with comment editing feature and no scrollbar
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt, faComment, faCommentSlash } from '@fortawesome/free-solid-svg-icons';
import { Modal, Button, Form } from 'react-bootstrap';
import SongPartPreview from '../../../songs/SongPartPreview';
import { updateSetlist } from '../../../../services/setlistService';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { getRoleLabel } from '../../../../constants/songRoles';

// Move the CommentModal outside the main component to prevent re-renders
const CommentModal = ({
  show,
  onHide,
  commentText,
  setCommentText,
  songTitle,
  onSave,
  isSaving,
  t
}) => (
  <Modal show={show} onHide={onHide}>
    <Modal.Header closeButton>
      <Modal.Title>
        {songTitle ? t('events.setlist.commentTitle', { songTitle }) : t('events.setlist.addComment')}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <Form.Group>
        <Form.Label>{t('events.setlist.addComment')}</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          placeholder={t('events.setlist.commentPlaceholder')}
        />
      </Form.Group>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onHide}>
        {t('events.setlist.cancel')}
      </Button>
      <Button
        variant="primary"
        onClick={onSave}
        disabled={isSaving}
      >
        {isSaving ? t('common.loading') : t('events.setlist.saveComment')}
      </Button>
    </Modal.Footer>
  </Modal>
);

function EventSetlist({ loading, setlist, eventData, isMobile = false, songsWithParts = {}, onUpdate }) {
    const { t } = useLanguage();
    const [selectedSong, setSelectedSong] = useState(null);
    const [showPartPreview, setShowPartPreview] = useState(false);

    // For comment editing feature
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [editingSong, setEditingSong] = useState(null);
    const [commentText, setCommentText] = useState('');
    const [savingComment, setSavingComment] = useState(false);
    
    // Handle click on a song title - view the part file
    const handleSongClick = (song) => {
        if (!song || !song.song_id) return;
        
        // Get full song data with has_part_file information
        const fullSongData = songsWithParts[song.song_id];
        
        // Only proceed if the song has a part file
        if (fullSongData && fullSongData.has_part_file) {
            // Include the comment from the setlist song
            const songForPreview = {
                ...fullSongData,
                id: song.song_id,
                comment: song.comment // Include the comment from the setlist
            };
            
            setSelectedSong(songForPreview);
            setShowPartPreview(true);
        }
    };
    
    // Handle closing the preview modal
    const handleClosePreview = () => {
        setShowPartPreview(false);
    };
    
    // Check if a song has a part file
    const hasPart = (song) => {
        if (!song || !song.song_id) return false;
        const fullSong = songsWithParts[song.song_id];
        return fullSong && fullSong.has_part_file;
    };
    
    // Open comment modal for editing
    const handleCommentClick = (e, song) => {
        e.stopPropagation(); // Prevent event bubbling to the song click handler
        setEditingSong(song);
        setCommentText(song.comment || '');
        setShowCommentModal(true);
    };
    
    // Save the comment to the setlist
    const handleSaveComment = async () => {
        if (!editingSong || !setlist || !setlist.id) return;

        setSavingComment(true);
        try {
            // Create a copy of the setlist's songs array
            const updatedSongs = setlist.songs.map(song => {
                if (song.song_id === editingSong.song_id) {
                    // Update this song's comment
                    return { ...song, comment: commentText };
                }
                return song;
            });

            // Update the setlist with the new songs array
            const updatedSetlist = {
                ...setlist,
                songs: updatedSongs
            };

            // Save to the server
            await updateSetlist(setlist.id, updatedSetlist);

            // Call the onUpdate callback if provided
            if (onUpdate) {
                onUpdate(updatedSetlist);
            }

            // Close the modal
            setShowCommentModal(false);
        } catch (error) {
            alert(t('events.messages.errorSavingCommentTryAgain'));
        } finally {
            setSavingComment(false);
        }
    };
    
    return (
        <>
            {loading ? (
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">{t('common.loading')}</span>
                    </div>
                </div>
            ) : setlist && setlist.songs && setlist.songs.length > 0 ? (
                <div className="setlist-container" style={{ maxHeight: 'none', overflowY: 'visible' }}>
                    {setlist.songs.map((song, index) => (
                        <div key={`${song.song_id}-${index}`} className="p-2 bg-light rounded mb-1">
                            <div className="d-flex justify-content-between align-items-center">
                                <small className="text-muted me-2" style={{
                                    fontSize: '0.8rem',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {getRoleLabel(song.song_role, t)}
                                </small>
                                <div className="d-flex align-items-center ms-auto" style={{ 
                                    maxWidth: '70%',
                                    textAlign: 'right'
                                }}>
                                    {/* Comment icon */}
                                    <FontAwesomeIcon 
                                        icon={song.comment ? faComment : faCommentSlash} 
                                        className={`${song.comment ? 'text-primary' : 'text-muted'} me-2`}
                                        style={{ 
                                            fontSize: '0.8rem',
                                            cursor: 'pointer'
                                        }}
                                        onClick={(e) => handleCommentClick(e, song)}
                                        title={song.comment ? t('events.setlist.editComment') : t('events.setlist.addComment')}
                                    />
                                    
                                    {/* Part file icon */}
                                    {hasPart(song) && (
                                        <FontAwesomeIcon 
                                            icon={faFileAlt} 
                                            className="text-primary me-1" 
                                            style={{ 
                                                fontSize: '0.8rem'
                                            }}
                                        />
                                    )}
                                    
                                    {/* Song title */}
                                    <span 
                                        className={`text-end ${hasPart(song) ? 'text-primary' : ''}`} 
                                        style={{ 
                                            fontSize: '1rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            cursor: hasPart(song) ? 'pointer' : 'default'
                                        }}
                                        onClick={() => handleSongClick(song)}
                                    >
                                        {song.title}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-muted text-center" style={{ fontSize: '1rem' }}>
                    {eventData.setlist_id ? t('events.setlist.empty') : t('events.setlist.notDefined')}
                </p>
            )}
            
            {/* Part Preview Modal - only render when needed */}
            {showPartPreview && selectedSong && (
                <SongPartPreview 
                    show={showPartPreview}
                    onHide={handleClosePreview}
                    song={selectedSong}
                />
            )}
            
            {/* Comment Edit Modal - Pass props to the external component */}
            <CommentModal
                show={showCommentModal}
                onHide={() => setShowCommentModal(false)}
                commentText={commentText}
                setCommentText={setCommentText}
                songTitle={editingSong?.title}
                onSave={handleSaveComment}
                isSaving={savingComment}
                t={t}
            />
        </>
    );
}

export default EventSetlist;